import { APP_PATHS } from "../../../shared/utils/app-route-paths";
import { TourKey } from "../models/tour-step.model";

/**
 * Every guide an admin can switch on or off, grouped the way the Guide Management screen
 * lists them. The steps themselves live in onboarding-tour-steps.config.ts — this is the
 * catalog, so a new tour only has to be added here to become manageable and to get its
 * help button in the header.
 */
export interface GuideCatalogEntry {
  key: TourKey;
  /** i18n key for the human name of the interface this guide walks through. */
  labelKey: string;
  /**
   * Router URL the guide belongs to. The header help button matches the current URL
   * against this (exact, or a child route); the most specific entry wins, so
   * `/messaging/contacts/new` picks the create guide over the list guide.
   *
   * Leave undefined for guides that only run as a sub-step of another screen — they stay
   * manageable in the admin list but never own the header button.
   */
  route?: string;
}

export interface GuideCatalogGroup {
  /** i18n key for the section heading. */
  labelKey: string;
  guides: GuideCatalogEntry[];
}

export const GUIDE_CATALOG: GuideCatalogGroup[] = [
  {
    labelKey: "GUIDE_ADMIN.GROUP.GETTING_STARTED",
    guides: [
      { key: "setupSteps", labelKey: "GUIDE_ADMIN.GUIDE.SETUP_STEPS", route: APP_PATHS.dashboardV0 },
    ],
  },
  {
    labelKey: "GUIDE_ADMIN.GROUP.MESSAGING",
    guides: [
      { key: "teamInbox", labelKey: "GUIDE_ADMIN.GUIDE.TEAM_INBOX", route: APP_PATHS.messaging.inbox },
      // Runs inside the inbox once a conversation is open — the inbox owns the header button.
      { key: "teamInboxConversation", labelKey: "GUIDE_ADMIN.GUIDE.TEAM_INBOX_CONVERSATION" },
      { key: "contacts", labelKey: "GUIDE_ADMIN.GUIDE.CONTACTS", route: APP_PATHS.messaging.contacts },
      { key: "contactsCreate", labelKey: "GUIDE_ADMIN.GUIDE.CONTACTS_CREATE", route: APP_PATHS.messaging.contactNew },
      { key: "contactsImport", labelKey: "GUIDE_ADMIN.GUIDE.CONTACTS_IMPORT", route: APP_PATHS.messaging.contactImport },
      { key: "contactsAttributes", labelKey: "GUIDE_ADMIN.GUIDE.CONTACTS_ATTRIBUTES", route: APP_PATHS.messaging.attributes },
      { key: "templates", labelKey: "GUIDE_ADMIN.GUIDE.TEMPLATES", route: APP_PATHS.workflow.templates },
      { key: "templateCreate", labelKey: "GUIDE_ADMIN.GUIDE.TEMPLATE_CREATE", route: APP_PATHS.workflow.templateRequestNew },
      { key: "flows", labelKey: "GUIDE_ADMIN.GUIDE.FLOWS", route: APP_PATHS.workflow.forms },
      { key: "flowCreate", labelKey: "GUIDE_ADMIN.GUIDE.FLOW_CREATE", route: APP_PATHS.workflow.formNew },
      { key: "broadcast", labelKey: "GUIDE_ADMIN.GUIDE.BROADCAST", route: APP_PATHS.campaigns.history },
      { key: "broadcastCreate", labelKey: "GUIDE_ADMIN.GUIDE.BROADCAST_CREATE", route: APP_PATHS.campaigns.new },
      // Steps of the campaign wizard — they run inside the create screen, which owns the button.
      { key: "broadcastCreateSchedule", labelKey: "GUIDE_ADMIN.GUIDE.BROADCAST_SCHEDULE" },
      { key: "broadcastCreateAudience", labelKey: "GUIDE_ADMIN.GUIDE.BROADCAST_AUDIENCE" },
      { key: "broadcastCreateMessage", labelKey: "GUIDE_ADMIN.GUIDE.BROADCAST_MESSAGE" },
      { key: "broadcastCreateReview", labelKey: "GUIDE_ADMIN.GUIDE.BROADCAST_REVIEW" },
    ],
  },
  {
    labelKey: "GUIDE_ADMIN.GROUP.IMPORTS",
    guides: [
      // Products, rooms, vouchers, FAQ… share the import screen; it also runs from the ⓘ of every import page.
      { key: "importWizard", labelKey: "GUIDE_ADMIN.GUIDE.IMPORT_WIZARD", route: APP_PATHS.catalog.productImport },
    ],
  },
  {
    labelKey: "GUIDE_ADMIN.GROUP.PAYMENT",
    guides: [
      { key: "paymentLinks", labelKey: "GUIDE_ADMIN.GUIDE.PAYMENT_LINKS", route: APP_PATHS.payment.links },
      { key: "paymentLinkCreate", labelKey: "GUIDE_ADMIN.GUIDE.PAYMENT_LINK_CREATE" },
      { key: "paymentAuthorizations", labelKey: "GUIDE_ADMIN.GUIDE.PAYMENT_AUTHORIZATIONS", route: APP_PATHS.payment.authorizations },
      { key: "paymentSavedCards", labelKey: "GUIDE_ADMIN.GUIDE.PAYMENT_SAVED_CARDS", route: APP_PATHS.payment.savedCards },
    ],
  },
  {
    labelKey: "GUIDE_ADMIN.GROUP.RESTAURANT",
    guides: [
      { key: "diningTables", labelKey: "GUIDE_ADMIN.GUIDE.DINING_TABLES", route: APP_PATHS.catalog.diningTables },
      // Runs inside the delivery method form (page or orders drawer), from its ⓘ button.
      { key: "deliveryRings", labelKey: "GUIDE_ADMIN.GUIDE.DELIVERY_RINGS" },
    ],
  },
  {
    labelKey: "GUIDE_ADMIN.GROUP.SETTINGS",
    guides: [
      { key: "generalSettings", labelKey: "GUIDE_ADMIN.GUIDE.GENERAL_SETTINGS", route: APP_PATHS.settings.company },
      { key: "generalSettingsWhatsapp", labelKey: "GUIDE_ADMIN.GUIDE.GENERAL_SETTINGS_WHATSAPP" },
      { key: "metaConnection", labelKey: "GUIDE_ADMIN.GUIDE.META_CONNECTION" },
      { key: "metaAccount", labelKey: "GUIDE_ADMIN.GUIDE.META_ACCOUNT" },
      { key: "metaPhones", labelKey: "GUIDE_ADMIN.GUIDE.META_PHONES" },
      { key: "metaCatalog", labelKey: "GUIDE_ADMIN.GUIDE.META_CATALOG" },
    ],
  },
];

/** Flat list, for lookups and the "x of y enabled" counter. */
export const GUIDE_CATALOG_ENTRIES: GuideCatalogEntry[] = GUIDE_CATALOG.flatMap((group) => group.guides);

export const GUIDE_CATALOG_KEYS: TourKey[] = GUIDE_CATALOG_ENTRIES.map((guide) => guide.key);

/** Routed entries, most specific first, so `/a/b` beats `/a` when both match. */
const ROUTED_ENTRIES: GuideCatalogEntry[] = GUIDE_CATALOG_ENTRIES.filter((guide) => !!guide.route).sort(
  (a, b) => (b.route as string).length - (a.route as string).length,
);

/**
 * The guide that belongs to a router URL, or undefined when the page has none — which is
 * what hides the header help button instead of offering a guide that does not exist.
 */
export function resolveGuideForUrl(url: string): GuideCatalogEntry | undefined {
  const path = (url.split("?")[0] || "").split("#")[0].replace(/\/+$/, "") || "/";
  return ROUTED_ENTRIES.find((guide) => path === guide.route || path.startsWith(`${guide.route}/`));
}
